import React, { useState, useEffect } from 'react';
import { Brain, Save, CheckCircle } from 'lucide-react';

export default function CareerMemoryPanel({ theme = 'dark', authToken, onCreditsUpdate }) {
  const T = theme === 'dark';
  
  const [preferredRole, setPreferredRole] = useState('');
  const [salaryTarget, setSalaryTarget] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [preferredStyle, setPreferredStyle] = useState('harvard_classic');
  const [targetCompaniesInput, setTargetCompaniesInput] = useState('');
  const [skillsInput, setSkillsInput] = useState('');
  const [careerGoalsInput, setCareerGoalsInput] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authToken}`
  };

  useEffect(() => {
    if (authToken) {
      fetchMemory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const fetchMemory = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/career/memory', { headers: authHeaders });
      if (!res.ok) throw new Error('Failed to load career memory');
      const data = await res.json();
      
      setPreferredRole(data.preferred_role || '');
      setSalaryTarget(data.salary_target || '');
      setExperienceLevel(data.experience_level || '');
      setPreferredStyle(data.preferred_style || 'harvard_classic');
      setTargetCompaniesInput((data.target_companies || []).join(', '));
      setSkillsInput((data.frequently_used_keywords || []).join(', '));
      setCareerGoalsInput((data.career_goals || []).join(', '));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMsg('');
    
    // Parse comma-separated inputs to clean arrays
    const target_companies = targetCompaniesInput
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
      
    const frequently_used_keywords = skillsInput
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
      
    const career_goals = careerGoalsInput
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const payload = {
      preferred_role: preferredRole,
      salary_target: salaryTarget,
      experience_level: experienceLevel,
      preferred_style: preferredStyle,
      target_companies,
      frequently_used_keywords,
      career_goals
    };

    try {
      const res = await fetch('/api/career/memory', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to update career memory');
      
      setSuccessMsg('Career memory updated successfully.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${T ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderTopColor: 'var(--brand)', animation: 'spin 1s linear infinite' }} />
        <p className={`text-xs mt-3 ${T ? 'text-zinc-400' : 'text-gray-500'}`}>Loading Career Memory Profile...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className={`border-b p-5 pb-3 shrink-0 ${T ? 'border-zinc-850 bg-black' : 'border-gray-200 bg-white'}`}>
        <h3 className={`text-sm font-bold flex items-center gap-2 ${T ? 'text-white' : 'text-gray-900'}`}>
          <Brain size={16} className="text-violet-500" />
          <span>Career Memory Profile</span>
        </h3>
        <p className={`text-[11px] mt-1 ${T ? 'text-zinc-400' : 'text-gray-500'}`}>
          Configure preferences that the AI Assistant persistently remembers to tailor your resumes and cover letters.
        </p>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-5" style={{ background: T ? '#09090b' : '#f9fafb' }}>
        <form onSubmit={handleSave} className="max-w-2xl mx-auto space-y-5">
          {error && (
            <div className="p-3 text-xs bg-red-500/10 border border-red-500/20 text-red-500 rounded-xl">
              ⚠️ {error}
            </div>
          )}
          
          {successMsg && (
            <div className="p-3 text-xs bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 rounded-xl flex items-center gap-2">
              <CheckCircle size={14} />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Preferences Grid */}
          <div className={`p-5 rounded-2xl border ${T ? 'bg-[#18181C] border-zinc-800' : 'bg-white border-gray-200'} space-y-4`}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-[11px] font-bold uppercase tracking-wider ${T ? 'text-zinc-400' : 'text-gray-500'} mb-2`}>
                  Preferred Job Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. Senior Backend Engineer"
                  value={preferredRole}
                  onChange={e => setPreferredRole(e.target.value)}
                  className={`text-xs px-3 py-2 border rounded-xl w-full ${T ? 'bg-zinc-950 border-zinc-850 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:border-violet-500`}
                />
              </div>

              <div>
                <label className={`block text-[11px] font-bold uppercase tracking-wider ${T ? 'text-zinc-400' : 'text-gray-500'} mb-2`}>
                  Target Salary
                </label>
                <input
                  type="text"
                  placeholder="e.g. 15 LPA / $140,000"
                  value={salaryTarget}
                  onChange={e => setSalaryTarget(e.target.value)}
                  className={`text-xs px-3 py-2 border rounded-xl w-full ${T ? 'bg-zinc-950 border-zinc-850 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:border-violet-500`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-[11px] font-bold uppercase tracking-wider ${T ? 'text-zinc-400' : 'text-gray-500'} mb-2`}>
                  Experience Level
                </label>
                <input
                  type="text"
                  placeholder="e.g. 3 Years, Fresher, Senior"
                  value={experienceLevel}
                  onChange={e => setExperienceLevel(e.target.value)}
                  className={`text-xs px-3 py-2 border rounded-xl w-full ${T ? 'bg-zinc-950 border-zinc-850 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:border-violet-500`}
                />
              </div>

              <div>
                <label className={`block text-[11px] font-bold uppercase tracking-wider ${T ? 'text-zinc-400' : 'text-gray-500'} mb-2`}>
                  Favorite Style
                </label>
                <select
                  value={preferredStyle}
                  onChange={e => setPreferredStyle(e.target.value)}
                  className={`text-xs px-3 py-2 border rounded-xl w-full ${T ? 'bg-zinc-950 border-zinc-850 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:border-violet-500`}
                >
                  <option value="harvard_classic">Harvard ATS Classic</option>
                  <option value="executive_professional">Executive Professional</option>
                  <option value="faang_engineering">FAANG / Developer Terminal</option>
                  <option value="modern_ats_pro">Modern Minimalist</option>
                </select>
              </div>
            </div>
          </div>

          {/* List Inputs */}
          <div className={`p-5 rounded-2xl border ${T ? 'bg-[#18181C] border-zinc-800' : 'bg-white border-gray-200'} space-y-4`}>
            <div>
              <label className={`block text-[11px] font-bold uppercase tracking-wider ${T ? 'text-zinc-400' : 'text-gray-500'} mb-2`}>
                Target Companies
              </label>
              <textarea
                placeholder="Google, Amazon, Microsoft (comma separated)"
                rows={2}
                value={targetCompaniesInput}
                onChange={e => setTargetCompaniesInput(e.target.value)}
                className={`text-xs px-3 py-2 border rounded-xl w-full ${T ? 'bg-zinc-950 border-zinc-850 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:border-violet-500 resize-none`}
              />
            </div>

            <div>
              <label className={`block text-[11px] font-bold uppercase tracking-wider ${T ? 'text-zinc-400' : 'text-gray-500'} mb-2`}>
                Core Competencies & Skills
              </label>
              <textarea
                placeholder="React, Java, Spring Boot, REST APIs (comma separated)"
                rows={3}
                value={skillsInput}
                onChange={e => setSkillsInput(e.target.value)}
                className={`text-xs px-3 py-2 border rounded-xl w-full ${T ? 'bg-zinc-950 border-zinc-850 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:border-violet-500 resize-none`}
              />
            </div>

            <div>
              <label className={`block text-[11px] font-bold uppercase tracking-wider ${T ? 'text-zinc-400' : 'text-gray-500'} mb-2`}>
                Current Goals
              </label>
              <textarea
                placeholder="Switch to frontend, land senior backend developer role (comma separated)"
                rows={2}
                value={careerGoalsInput}
                onChange={e => setCareerGoalsInput(e.target.value)}
                className={`text-xs px-3 py-2 border rounded-xl w-full ${T ? 'bg-zinc-950 border-zinc-850 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:outline-none focus:border-violet-500 resize-none`}
              />
            </div>
          </div>

          {/* Action Footer */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-xs px-5 py-2.5 rounded-xl flex items-center gap-2 hover:bg-violet-600 font-bold active:scale-[0.98] transition-all"
            >
              <Save size={14} />
              <span>{saving ? 'Saving...' : 'Update Career Memory'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
